import { Check, ChevronRight, FileText } from "lucide-react";
import type { ReactNode } from "react";
import { Link } from "react-router";

import { cn } from "@/ui/cn";

export type FlowStep = "upload" | "sheets" | "processing" | "review" | "takeoff";

const STEPS: readonly { key: FlowStep; title: string }[] = [
  { key: "upload", title: "Upload" },
  { key: "sheets", title: "Select sheets" },
  { key: "processing", title: "Process" },
  { key: "review", title: "Review" },
  { key: "takeoff", title: "Take-off" },
];

interface Props {
  step: FlowStep;
  documentId?: string | undefined;
  filename?: string | undefined;
  /** The run the last two steps belong to, once there is one. */
  runId?: string | null | undefined;
  /** True when that run has finished, so the take-off step can be opened. */
  runFinished?: boolean | undefined;
  /** The run has a sheet workspace to review (ADR-0020); otherwise the step is not shown. */
  review?: boolean | undefined;
  children?: ReactNode;
}

function target(
  key: FlowStep,
  documentId: string | undefined,
  runId: string | null | undefined,
  runFinished: boolean | undefined,
): string | null {
  if (key === "upload") {
    return "/sets/new";
  }
  if (!documentId) {
    return null;
  }
  if (key === "sheets") {
    return `/documents/${documentId}/sheets`;
  }
  if (!runId) {
    return null;
  }
  if (key === "processing") {
    return `/documents/${documentId}/runs/${runId}/progress`;
  }
  if (key === "review") {
    return runFinished ? `/documents/${documentId}/runs/${runId}/review` : null;
  }
  return runFinished ? `/documents/${documentId}/runs/${runId}/takeoff` : null;
}

/**
 * The bar across the top of every step, inside the app shell: what set this is and
 * where in the four steps the estimator stands. The steps are a real sequence, so they
 * are numbered. The logo and theme belong to the shell's sidebar.
 */
export function StepHeader({
  step,
  documentId,
  filename,
  runId,
  runFinished,
  review,
  children,
}: Props) {
  const steps = STEPS.filter((s) => s.key !== "review" || review || step === "review");
  const at = steps.findIndex((s) => s.key === step);
  return (
    <header className="sticky top-0 z-30 border-b border-line bg-surface/95 backdrop-blur">
      <div className="mx-auto flex h-14 max-w-[1600px] items-center gap-4 px-4 lg:px-6">
        <div className="flex min-w-0 flex-1 items-center gap-3">
          {filename ? (
            <>
              <span
                className="flex min-w-0 items-center gap-1.5 text-sm text-ink-2"
                title={filename}
              >
                <FileText aria-hidden="true" className="size-4 shrink-0 text-muted" />
                <span className="truncate">{filename}</span>
              </span>
            </>
          ) : null}
        </div>

        <nav aria-label="Steps" className="hidden md:block">
          <ol className="flex items-center gap-1">
            {steps.map((s, i) => {
              const state = i < at ? "done" : i === at ? "current" : "ahead";
              const to = i === at ? null : target(s.key, documentId, runId, runFinished);
              const body = (
                <>
                  <span
                    aria-hidden="true"
                    className={cn(
                      "grid size-5 place-items-center rounded-full text-[11px] font-semibold",
                      state === "current" && "bg-accent text-accent-ink",
                      state === "done" && "bg-accent-soft text-accent",
                      state === "ahead" && "border border-line-strong text-muted",
                    )}
                  >
                    {state === "done" ? <Check className="size-3" strokeWidth={3} /> : i + 1}
                  </span>
                  <span>{s.title}</span>
                </>
              );
              const look = cn(
                "flex items-center gap-2 rounded-md px-2 py-1 text-[13px] no-underline",
                state === "current" ? "font-semibold text-ink" : "text-muted",
              );
              return (
                <li
                  key={s.key}
                  className="flex items-center gap-1"
                  data-state={state}
                  {...(state === "current" ? { "aria-current": "step" as const } : {})}
                >
                  {i > 0 ? (
                    <ChevronRight aria-hidden="true" className="size-4 text-line-strong" />
                  ) : null}
                  {to ? (
                    <Link to={to} className={cn(look, "hover:bg-sunken hover:text-ink")}>
                      {body}
                    </Link>
                  ) : (
                    <span className={look}>{body}</span>
                  )}
                </li>
              );
            })}
          </ol>
        </nav>

        <div className="flex flex-1 items-center justify-end gap-2">{children}</div>
      </div>
    </header>
  );
}
